import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { DatabaseBackup, Play, Download, ShieldCheck, RefreshCcw, CheckCircle2, XCircle, Clock, Cloud, CloudOff, RotateCcw, AlertTriangle, FileCode } from 'lucide-react';

interface BackupRun {
  id: string; kind: string; status: string; started_at: string; finished_at?: string;
  size_bytes?: number; object_key?: string; item_id?: string; error?: string; triggered_by?: string;
  last_test_restore?: { ok: boolean; collections: Array<{ collection: string; live: number; restored: number; match: boolean }> };
}
interface OneDriveStatus { configured: boolean; enabled: boolean; owner_email: string | null }
interface Config {
  folder_path: string;
  passphrase_set: boolean;
  schedule: { enabled: boolean; hourly_enabled: boolean; code_enabled: boolean; daily_time: string; tz: string };
  retention: { hourly: number; daily: number; weekly: number; monthly: number; annual: number; code: number; manual: number };
  onedrive: OneDriveStatus;
}
interface Available { item_id: string; name: string; kind: string; size_bytes?: number; last_modified?: string; web_url?: string }

const fmtBytes = (n?: number): string => {
  if (!n) return '—';
  const u = ['B', 'KB', 'MB', 'GB']; let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${u[i]}`;
};
const errMsg = (e: unknown, fallback: string): string =>
  (e as { response?: { data?: { detail?: string; error?: string } } })?.response?.data?.detail
  ?? (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback;

const StatusBadge: React.FC<{ s: string }> = ({ s }) => {
  if (s === 'success') return <Badge variant="success"><CheckCircle2 size={12} className="mr-1" />Success</Badge>;
  if (s === 'failed') return <Badge variant="destructive"><XCircle size={12} className="mr-1" />Failed</Badge>;
  return <Badge variant="warning"><Clock size={12} className="mr-1" />Running</Badge>;
};

export const BackupsPage: React.FC = () => {
  const [cfg, setCfg] = useState<Config | null>(null);
  const [runs, setRuns] = useState<BackupRun[]>([]);
  const [avail, setAvail] = useState<Available[]>([]);
  const [form, setForm] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string>('');
  const [confirmItem, setConfirmItem] = useState<Available | null>(null);

  const load = useCallback(async () => {
    try {
      const [c, h] = await Promise.all([api.get<Config>('/backups/config'), api.get<BackupRun[]>('/backups/history')]);
      setCfg(c.data); setRuns(h.data ?? []);
    } catch { toast.error('Failed to load backups'); }
  }, []);
  const loadAvailable = useCallback(async () => {
    try { const r = await api.get<Available[]>('/backups/available'); setAvail(r.data ?? []); }
    catch (e: unknown) { toast.error(errMsg(e, 'Could not list OneDrive backups')); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const running = runs.some(r => r.status === 'running');
    if (!running) return;
    const t = setInterval(() => { void load(); }, 4000);
    return () => clearInterval(t);
  }, [runs, load]);

  const saveCreds = async (): Promise<void> => {
    setBusy('save');
    try { await api.put('/backups/config', form); toast.success('Backup settings saved'); setForm({}); await load(); }
    catch (e: unknown) { toast.error(errMsg(e, 'Save failed')); }
    finally { setBusy(''); }
  };
  const saveSchedule = async (patch: object): Promise<void> => {
    try { const r = await api.put<Config>('/backups/config', patch); setCfg(r.data); toast.success('Updated'); }
    catch { toast.error('Update failed'); }
  };
  const backupNow = async (): Promise<void> => {
    setBusy('run');
    try { await api.post('/backups/run'); toast.success('Backup started'); await load(); }
    catch { toast.error('Could not start backup'); } finally { setBusy(''); }
  };
  const backupCodeNow = async (): Promise<void> => {
    setBusy('code');
    try { await api.post('/backups/run-code'); toast.success('Code snapshot started'); await load(); }
    catch { toast.error('Could not start code snapshot'); } finally { setBusy(''); }
  };
  const streamDownload = async (url: string, filename: string): Promise<void> => {
    const r = await api.get(url, { responseType: 'blob' });
    const href = window.URL.createObjectURL(r.data as Blob);
    const a = document.createElement('a'); a.href = href; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(href);
  };
  const downloadLatest = async (): Promise<void> => {
    setBusy('dl');
    try { await streamDownload('/backups/latest/download', 'raptor-latest.archive.gz.enc'); }
    catch (e: unknown) { toast.error(errMsg(e, 'No backup available')); }
    finally { setBusy(''); }
  };
  const testRestore = async (item_id?: string): Promise<void> => {
    setBusy('restore');
    try {
      const r = await api.post<BackupRun['last_test_restore']>('/backups/test-restore', item_id ? { item_id } : {});
      toast[r.data?.ok ? 'success' : 'error'](r.data?.ok ? 'Test restore matched all row counts ✓' : 'Test restore found mismatches');
      await load();
    } catch (e: unknown) { toast.error(errMsg(e, 'Test restore failed')); }
    finally { setBusy(''); }
  };
  const doRestore = async (): Promise<void> => {
    if (!confirmItem) return;
    setBusy('live-restore');
    try {
      await api.post('/backups/restore', { item_id: confirmItem.item_id, confirm: true });
      toast.success('Live database restored from OneDrive');
      setConfirmItem(null); await load();
    } catch (e: unknown) { toast.error(errMsg(e, 'Restore failed')); }
    finally { setBusy(''); }
  };

  if (!cfg) return <div className="p-6 text-raptor-navy/60">Loading…</div>;
  const latestTest = runs.find(r => r.last_test_restore)?.last_test_restore;
  const od = cfg.onedrive;
  const odReady = od.configured && od.enabled;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="backups-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-headline text-3xl text-raptor-navy tracking-wide flex items-center gap-2"><DatabaseBackup size={26} /> Backups</h2>
          <p className="text-sm text-raptor-navy/60 font-body">Encrypted database backups to OneDrive (Microsoft 365). Super Admin only.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="gradient" onClick={backupNow} disabled={busy === 'run'} data-testid="backup-now-btn"><Play size={15} className="mr-1.5" />{busy === 'run' ? 'Starting…' : 'Back up now'}</Button>
          <Button variant="outline" onClick={backupCodeNow} disabled={busy === 'code'} data-testid="backup-code-btn"><FileCode size={15} className="mr-1.5" />{busy === 'code' ? 'Starting…' : 'Back up code'}</Button>
          <Button variant="outline" onClick={downloadLatest} disabled={busy === 'dl'} data-testid="download-latest-btn"><Download size={15} className="mr-1.5" />Download latest</Button>
          <Button variant="outline" onClick={() => testRestore()} disabled={busy === 'restore'} data-testid="test-restore-btn"><ShieldCheck size={15} className="mr-1.5" />{busy === 'restore' ? 'Restoring…' : 'Test restore'}</Button>
          <Button variant="ghost" size="icon" onClick={() => load()} title="Refresh"><RefreshCcw size={16} /></Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2">OneDrive storage</CardTitle><CardDescription>Backups reuse your Microsoft 365 integration. Leave the passphrase blank to keep the current value.</CardDescription></CardHeader>
          <CardContent className="space-y-3">
            <div className={`flex items-center gap-2 rounded-md border px-3 py-2.5 text-sm ${odReady ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-amber-300 bg-amber-50 text-amber-800'}`} data-testid="onedrive-status">
              {odReady ? <Cloud size={18} /> : <CloudOff size={18} />}
              <div className="leading-tight">
                {odReady
                  ? <><span className="font-semibold">Connected</span> · {od.owner_email}</>
                  : <><span className="font-semibold">Not ready</span> — {od.configured ? 'OneDrive integration is disabled.' : 'OneDrive integration is not configured.'} Set it up under Integrations.</>}
              </div>
            </div>
            <div className="space-y-1.5"><Label>Backup folder (in OneDrive)</Label><Input data-testid="folder-path" defaultValue={cfg.folder_path} onChange={(e) => setForm(f => ({ ...f, folder_path: e.target.value }))} placeholder="RAPTOR/Backups" /></div>
            <div className="space-y-1.5"><Label>Encryption passphrase {cfg.passphrase_set && <Badge variant="muted" className="ml-1">set</Badge>}</Label><Input data-testid="passphrase" type="password" placeholder={cfg.passphrase_set ? '•••••• (unchanged)' : 'Required for encryption'} onChange={(e) => setForm(f => ({ ...f, passphrase: e.target.value }))} /></div>
            <div className="flex justify-end"><Button variant="gradient" onClick={saveCreds} disabled={busy === 'save' || Object.keys(form).length === 0} data-testid="save-creds-btn">Save settings</Button></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Schedule &amp; retention</CardTitle><CardDescription>Nightly at {cfg.schedule.daily_time} {cfg.schedule.tz}. After each run, only the newest N archives per type are kept — older ones are deleted from OneDrive.</CardDescription></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Nightly schedule enabled</Label>
              <Button variant={cfg.schedule.enabled ? 'gradient' : 'outline'} size="sm" onClick={() => saveSchedule({ schedule: { enabled: !cfg.schedule.enabled } })} data-testid="toggle-schedule">{cfg.schedule.enabled ? 'On' : 'Off'}</Button>
            </div>
            <div className="space-y-1.5"><Label>Daily run time (SAST)</Label><Input type="time" defaultValue={cfg.schedule.daily_time} onBlur={(e) => saveSchedule({ schedule: { daily_time: e.target.value } })} data-testid="daily-time" /></div>
            <div className="flex items-center justify-between">
              <div><Label>Hourly backups</Label><p className="text-xs text-raptor-navy/50">Runs a backup at the top of every hour. Increases OneDrive usage.</p></div>
              <Button variant={cfg.schedule.hourly_enabled ? 'gradient' : 'outline'} size="sm" onClick={() => saveSchedule({ schedule: { hourly_enabled: !cfg.schedule.hourly_enabled } })} data-testid="toggle-hourly">{cfg.schedule.hourly_enabled ? 'On' : 'Off'}</Button>
            </div>
            <div className="flex items-center justify-between">
              <div><Label>Daily code snapshot</Label><p className="text-xs text-raptor-navy/50">Archives the app source to OneDrive at the daily run time.</p></div>
              <Button variant={cfg.schedule.code_enabled ? 'gradient' : 'outline'} size="sm" onClick={() => saveSchedule({ schedule: { code_enabled: !cfg.schedule.code_enabled } })} data-testid="toggle-code">{cfg.schedule.code_enabled ? 'On' : 'Off'}</Button>
            </div>
            <div>
              <Label className="mb-1.5 block">Retention policy — archives kept per type</Label>
              <div className="grid grid-cols-3 gap-3">
                {(['hourly', 'daily', 'weekly', 'monthly', 'annual', 'code', 'manual'] as const).map(k => (
                  <div key={k} className="space-y-1.5">
                    <Label className="capitalize text-xs">{k}</Label>
                    <Input type="number" min={1} defaultValue={cfg.retention[k]} onBlur={(e) => saveSchedule({ retention: { [k]: Number(e.target.value) } })} data-testid={`retention-${k}`} />
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card data-testid="restore-card">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div><CardTitle className="flex items-center gap-2"><RotateCcw size={18} /> Restore from OneDrive</CardTitle><CardDescription>List archives stored in OneDrive, verify a backup, or overwrite the live database.</CardDescription></div>
          <Button variant="outline" size="sm" onClick={loadAvailable} disabled={!odReady} data-testid="list-backups-btn"><Cloud size={14} className="mr-1.5" />List backups</Button>
        </CardHeader>
        <CardContent>
          {avail.length === 0 ? (
            <p className="text-sm text-raptor-navy/50 py-3">Click "List backups" to load archives currently stored in OneDrive.</p>
          ) : (
            <table className="w-full text-sm font-body">
              <thead><tr className="text-left text-raptor-navy/60 border-b border-border/60"><th className="py-2 px-2">Archive</th><th className="px-2">Type</th><th className="px-2">Size</th><th className="px-2">Modified</th><th className="px-2 text-right">Actions</th></tr></thead>
              <tbody data-testid="available-backups">
                {avail.map(a => (
                  <tr key={a.item_id} className="border-b border-border/40">
                    <td className="py-2 px-2 font-tabular text-xs truncate max-w-[220px]">{a.name}</td>
                    <td className="px-2 capitalize">{a.kind}</td>
                    <td className="px-2 font-tabular">{fmtBytes(a.size_bytes)}</td>
                    <td className="px-2 font-tabular text-xs">{a.last_modified ? new Date(a.last_modified).toLocaleString() : '—'}</td>
                    <td className="px-2">
                      <div className="flex justify-end gap-1.5">
                        <Button variant="ghost" size="sm" title="Download" onClick={() => streamDownload(`/backups/${a.item_id}/download`, a.name)} data-testid={`dl-${a.item_id}`}><Download size={14} /></Button>
                        {a.kind !== 'code' && <>
                          <Button variant="ghost" size="sm" title="Verify (non-destructive)" onClick={() => testRestore(a.item_id)} disabled={busy === 'restore'} data-testid={`verify-${a.item_id}`}><ShieldCheck size={14} /></Button>
                          <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" title="Restore over live DB" onClick={() => setConfirmItem(a)} data-testid={`restore-${a.item_id}`}><RotateCcw size={14} className="mr-1" />Restore</Button>
                        </>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {confirmItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="restore-confirm">
          <Card className="max-w-md w-full">
            <CardHeader><CardTitle className="flex items-center gap-2 text-red-600"><AlertTriangle size={20} /> Overwrite live database?</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-raptor-navy/80">This will <strong>drop and replace</strong> every collection in the live database with the contents of:</p>
              <p className="text-xs font-tabular bg-muted rounded px-2 py-1.5 break-all">{confirmItem.name}</p>
              <p className="text-sm text-red-600">This cannot be undone. Consider running a fresh backup and a "Verify" first.</p>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setConfirmItem(null)} disabled={busy === 'live-restore'} data-testid="restore-cancel">Cancel</Button>
                <Button variant="destructive" onClick={doRestore} disabled={busy === 'live-restore'} data-testid="restore-confirm-btn">{busy === 'live-restore' ? 'Restoring…' : 'Yes, overwrite live DB'}</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {latestTest && (
        <Card data-testid="test-restore-report">
          <CardHeader><CardTitle className="flex items-center gap-2">Last test restore {latestTest.ok ? <Badge variant="success">All matched</Badge> : <Badge variant="destructive">Mismatch</Badge>}</CardTitle><CardDescription>Non-destructive scratch restore with per-collection row-count comparison.</CardDescription></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm font-body">
              {latestTest.collections.map(c => (
                <div key={c.collection} className={`flex items-center justify-between px-2 py-1 rounded border ${c.match ? 'border-border' : 'border-red-300 bg-red-50'}`}>
                  <span className="truncate text-raptor-navy/70">{c.collection}</span>
                  <span className="font-tabular">{c.live}/{c.restored}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>Backup history</CardTitle><CardDescription>Most recent runs.</CardDescription></CardHeader>
        <CardContent>
          <table className="w-full text-sm font-body">
            <thead><tr className="text-left text-raptor-navy/60 border-b border-border/60"><th className="py-2 px-2">Started</th><th className="px-2">Type</th><th className="px-2">Status</th><th className="px-2">Size</th><th className="px-2">Location</th></tr></thead>
            <tbody data-testid="backup-history">
              {runs.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-raptor-navy/50">No backups yet. Set a passphrase and click "Back up now".</td></tr>}
              {runs.map(r => (
                <tr key={r.id} className="border-b border-border/40">
                  <td className="py-2 px-2 font-tabular text-xs">{new Date(r.started_at).toLocaleString()}</td>
                  <td className="px-2 capitalize">{r.kind}</td>
                  <td className="px-2"><StatusBadge s={r.status} /></td>
                  <td className="px-2 font-tabular">{fmtBytes(r.size_bytes)}</td>
                  <td className="px-2 text-xs text-raptor-navy/60 truncate max-w-[280px]">{r.error ? <span className="text-red-600">{r.error}</span> : (r.object_key || '—')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

export default BackupsPage;
